/**
 * 앱 세션 수명주기: 설정 → 잠금 해제 → (migration, 일일 스냅샷) → 사용 → 잠금.
 * UI는 이 모듈만 호출한다. 잠금 시 키와 Repository 참조를 버린다.
 */
import { appMigrationRegistry } from '@/domain/migrations';
import { exportBackup, restoreBackup } from '@/data/backup';
import type { DataKey } from '@/data/crypto/envelope';
import { chooseKdfParams } from '@/data/crypto/kdf';
import type { VaultHeader } from '@/data/crypto/vault';
import { CounselDb, META } from '@/data/db/schema';
import { verifyAll, type IntegrityReport } from '@/data/integrity';
import { runMigrations } from '@/data/migrationRunner';
import { joinRemoteVault } from '@/data/sync/join';
import type { RemoteStore } from '@/data/sync/remote';
import { loadRefreshToken, saveRefreshToken } from '@/data/sync/tokenStore';
import { getRefreshToken, setRefreshToken } from '@/platform/googleAuth';
import { Repository, type RepoContext } from '@/data/repo/repository';
import { createSnapshot, listSnapshots, restoreSnapshot } from '@/data/snapshots';
import {
  changeVaultPassphrase,
  getMeta,
  getOrCreateDeviceId,
  initializeVault,
  readVaultHeader,
  recoverVault,
  setMeta,
  unlockVault,
} from '@/data/vaultStore';

export const DEFAULT_AUTO_LOCK_MINUTES = 10;
export const BACKUP_REMINDER_DAYS = 7;
const DAY_MS = 86_400_000;

export type AppSession = {
  db: CounselDb;
  header: VaultHeader;
  deviceId: string;
  repo: Repository;
  ctx: RepoContext;
};

let dbSingleton: CounselDb | null = null;
export function getDb(): CounselDb {
  dbSingleton ??= new CounselDb('counselai');
  return dbSingleton;
}

export async function vaultStatus(db = getDb()): Promise<'uninitialized' | 'locked'> {
  return (await readVaultHeader(db)) ? 'locked' : 'uninitialized';
}

async function startSession(db: CounselDb, header: VaultHeader, dataKey: DataKey): Promise<AppSession> {
  const deviceId = await getOrCreateDeviceId(db);
  const ctx: RepoContext = { db, dataKey, vaultId: header.vaultId, deviceId };
  // 스키마 migration (필요할 때만, 실패하면 원본 유지 + 오류 전파)
  await runMigrations(ctx, appMigrationRegistry());
  // 하루 1회 자동 스냅샷 (계획서 §17-4)
  const last = await getMeta<string | null>(db, META.lastSnapshotAt, null);
  if ((await db.entities.count()) > 0 && (!last || Date.now() - Date.parse(last) > DAY_MS)) {
    await createSnapshot(db, 'daily');
  }
  // Google 갱신 토큰(암호화 저장)을 잠금 해제된 동안만 메모리에 올린다
  const memory = getRefreshToken();
  if (memory) await saveRefreshToken(ctx, memory);
  else setRefreshToken(await loadRefreshToken(ctx));
  return { db, header, deviceId, repo: new Repository(ctx), ctx };
}

/**
 * 새 기기: Google Drive에 있는 기존 데이터를 가져온다.
 * (호출 전에 사용자 클릭으로 Google 연결을 마쳐야 한다)
 */
export async function joinFromDrive(passphrase: string, remote: RemoteStore, db = getDb()): Promise<AppSession> {
  const { header, dataKey } = await joinRemoteVault(db, remote, passphrase);
  await requestPersistentStorage();
  const session = await startSession(db, header, dataKey);
  await db.meta.put({ key: META.driveConnected, value: true });
  return session;
}

export async function setupVault(
  passphrase: string,
  db = getDb(),
): Promise<{ session: AppSession; recoveryKey: string }> {
  const kdf = await chooseKdfParams();
  const created = await initializeVault(db, passphrase, kdf);
  await requestPersistentStorage();
  return { session: await startSession(db, created.header, created.dataKey), recoveryKey: created.recoveryKeyDisplay };
}

export async function unlock(passphrase: string, db = getDb()): Promise<AppSession> {
  const { header, dataKey } = await unlockVault(db, passphrase);
  return startSession(db, header, dataKey);
}

// ---------- 이 기기에서 로그인 유지 ----------

export const REMEMBER_DAYS = 30;
type Remembered = { key: CryptoKey; kid: string; vaultId: string; expiresAt: string; accountName?: string };

/**
 * 로그인 유지: 잠금 해제된 데이터 키(내보낼 수 없는 CryptoKey)를 이 기기의 IndexedDB에만 보관한다.
 * 키 값 자체는 꺼낼 수 없지만, 이 기기·브라우저를 쓰는 사람은 비밀번호 없이 열 수 있다 (공용 기기에서는 쓰지 않음).
 * 마지막 사용 후 30일이 지나거나 로그아웃하면 지워진다.
 */
export async function rememberDevice(session: AppSession, accountName?: string, days = REMEMBER_DAYS): Promise<void> {
  const value: Remembered = {
    key: session.ctx.dataKey.key,
    kid: session.ctx.dataKey.kid,
    vaultId: session.header.vaultId,
    expiresAt: new Date(Date.now() + days * 86_400_000).toISOString(),
    ...(accountName ? { accountName } : {}),
  };
  await session.db.meta.put({ key: META.remembered, value });
}

export async function forgetDevice(db = getDb()): Promise<void> {
  await db.meta.delete(META.remembered);
}

export async function isRemembered(db = getDb()): Promise<boolean> {
  return !!(await db.meta.get(META.remembered));
}

/** 기억된 기기면 비밀번호 없이 연다. 만료·다른 vault·키 불일치면 기억을 지우고 null */
export async function resumeRemembered(db = getDb()): Promise<{ session: AppSession; accountName?: string } | null> {
  const rec = (await db.meta.get(META.remembered))?.value as Remembered | undefined;
  if (!rec) return null;
  const header = await readVaultHeader(db);
  if (
    !header ||
    rec.vaultId !== header.vaultId ||
    rec.kid !== header.kid ||
    Date.parse(rec.expiresAt) < Date.now() ||
    !(rec.key instanceof CryptoKey)
  ) {
    await forgetDevice(db);
    return null;
  }
  try {
    const session = await startSession(db, header, { key: rec.key, kid: rec.kid });
    await rememberDevice(session, rec.accountName); // 쓸 때마다 30일 연장
    return { session, ...(rec.accountName ? { accountName: rec.accountName } : {}) };
  } catch {
    await forgetDevice(db);
    return null;
  }
}

export async function recover(recoveryKey: string, newPassphrase: string, db = getDb()): Promise<AppSession> {
  const { header, dataKey } = await recoverVault(db, recoveryKey, newPassphrase);
  return startSession(db, header, dataKey);
}

export async function changePassphrase(session: AppSession, oldP: string, newP: string): Promise<AppSession> {
  await changeVaultPassphrase(session.db, oldP, newP);
  const header = (await readVaultHeader(session.db))!;
  return { ...session, header };
}

/** 새 passphrase로 실제로 열리는지 확인 (세션은 만들지 않음) */
export async function verifyPassphrase(passphrase: string, db = getDb()): Promise<void> {
  await unlockVault(db, passphrase);
}

export async function getAutoLockMinutes(db = getDb()): Promise<number> {
  return getMeta(db, META.autoLockMinutes, DEFAULT_AUTO_LOCK_MINUTES);
}

export async function setAutoLockMinutes(minutes: number, db = getDb()): Promise<void> {
  await setMeta(db, META.autoLockMinutes, Math.min(120, Math.max(1, Math.round(minutes))));
}

// ---------- 백업 ----------

export async function makeBackupFile(session: AppSession): Promise<{ fileName: string; blob: Blob }> {
  const { fileName, text } = await exportBackup(session.db, session.ctx.dataKey);
  return { fileName, blob: new Blob([text], { type: 'application/octet-stream' }) };
}

export async function restoreFromBackupFile(text: string, passphrase: string, db = getDb()): Promise<AppSession> {
  const { header, dataKey } = await restoreBackup(db, text, passphrase);
  return startSession(db, header, dataKey);
}

export async function backupStatus(
  db = getDb(),
): Promise<{ lastBackupAt: string | null; overdue: boolean; hasData: boolean }> {
  const lastBackupAt = await getMeta<string | null>(db, META.lastBackupAt, null);
  const hasData = (await db.entities.count()) > 0;
  // 한 번도 백업하지 않았다면 vault 생성 시점부터 계산 (처음 쓰자마자 경고하지 않음)
  const since = lastBackupAt ?? (await readVaultHeader(db))?.createdAt ?? null;
  const overdue = hasData && !!since && Date.now() - Date.parse(since) > BACKUP_REMINDER_DAYS * DAY_MS;
  return { lastBackupAt, overdue, hasData };
}

// ---------- 점검·스냅샷 ----------

export function checkIntegrity(session: AppSession): Promise<IntegrityReport> {
  return verifyAll(session.db, session.repo);
}

export { listSnapshots, restoreSnapshot };

// ---------- 저장소 영속성 ----------

export async function requestPersistentStorage(): Promise<boolean> {
  try {
    return (await navigator.storage?.persist?.()) ?? false;
  } catch {
    return false;
  }
}

export async function storageInfo(): Promise<{ persisted: boolean | null; usage?: number; quota?: number }> {
  try {
    const persisted = (await navigator.storage?.persisted?.()) ?? null;
    const est = await navigator.storage?.estimate?.();
    return {
      persisted,
      ...(est?.usage !== undefined ? { usage: est.usage } : {}),
      ...(est?.quota !== undefined ? { quota: est.quota } : {}),
    };
  } catch {
    return { persisted: null };
  }
}
